'use client'
import React, { ComponentProps } from 'react'

import { Anchor, IconButton } from '@dynamo-caffeina-dev/airc-ui-kit'

import Icons from '@/components/atoms/Icons'
import {
  LayoutDirectionType,
  PatchedMenuItemType,
  PatchedSocialMediaMenuItemType,
} from '@/types'
import { usePathname } from 'next/navigation'
import {
  ExternalLinkMenuItemType,
  Maybe,
  PageLinkMenuItemType,
} from '@/types/__generated__'

const isSocialMediaMenuItem = (
  item: PatchedMenuItemType
): item is PatchedSocialMediaMenuItemType => !!item && 'social' in item

const isExternalLinkMenuItem = (
  item: PatchedMenuItemType
): item is ExternalLinkMenuItemType => !!item && 'url' in item

const isPageLinkMenuItem = (
  item: PatchedMenuItemType
): item is PageLinkMenuItemType => !!item && 'page' in item

export type NavigationOwnProps = {
  menu: {
    direction: LayoutDirectionType
    items?: Maybe<PatchedMenuItemType[]>
  }
}

const Navigation = ({ menu }: NavigationOwnProps) => {
  const pathname = usePathname()
  const { items, direction } = menu ?? {}

  function isActiveMenuItem(menuItem: PageLinkMenuItemType) {
    const { slug } = menuItem?.page ?? {}

    if (!slug) return false

    return pathname === '/' + slug
  }

  return (
    <ul
      className='airc-navigation--menu-list'
      data-direction={direction ?? ''}
    >
      {items?.map?.((menuItem: PatchedMenuItemType, index: number) => {
        const { label, target, variant } = menuItem

        const { social } = isSocialMediaMenuItem(menuItem) ? menuItem : {}

        const href = isExternalLinkMenuItem(menuItem)
          ? menuItem.url
          : isPageLinkMenuItem(menuItem)
            ? `/${menuItem.page?.slug ?? ''}`
            : '#'

        return (
          <li key={index}>
            {!social && (
              <Anchor
                {...(isPageLinkMenuItem(menuItem)
                  ? {
                      'data-active': isActiveMenuItem(menuItem),
                    }
                  : {})}
                variant={variant as ComponentProps<typeof Anchor>['variant']}
                className='airc-navigation--menu-list__item'
                href={href}
                // `rel` in case of `target: _blank`
                // is handled automatically by Anchor
                target={target}
              >
                {label}
              </Anchor>
            )}

            {social && (
              <Anchor
                href={social.url}
                target={target}
                variant={variant as ComponentProps<typeof Anchor>['variant']}
              >
                <IconButton
                  variant='bare'
                  title={`Condividi su ${label}`}
                >
                  {React.createElement(
                    Icons[label as PatchedSocialMediaMenuItemType['label']]
                  )}
                </IconButton>
              </Anchor>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default Navigation
